import {
	useGetMonthlyOverviewRootsLazyQuery,
	useGetMonthlyOverviewChildrenLazyQuery,
	useSearchMonthlyOverviewLazyQuery,
	OverviewItemType as GqlOverviewItemType,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useDebounce, type IdWithName } from "@repo/shared/common";
import { useCallback, useState, useMemo, useEffect } from "react";
import dayjs, { type Dayjs } from "dayjs";

import type { MonthlyProjectOverviewItem, OverviewItemType } from "../types/MonthlyProjectOverviewTypes";

export type PaginationModel = {
	page: number;
	pageSize: number;
};

export type UseMonthlyOverviewListReturn = {
	// Data
	rows: MonthlyProjectOverviewItem[];
	subRows: MonthlyProjectOverviewItem[];
	allRows: MonthlyProjectOverviewItem[];
	rowCount: number;
	isLoading: boolean;
	matchedProjectIds: string[];

	// Filters
	month: Dayjs;
	setMonth: (month: Dayjs) => void;
	searchValue: string;
	setSearchValue: (value: string) => void;
	debouncedSearchValue: string;
	selectedClient: IdWithName | null;
	setSelectedClient: (client: IdWithName | null) => void;
	selectedEmployee: IdWithName | null;
	setSelectedEmployee: (employee: IdWithName | null) => void;
	excludeEmptyTenders: boolean;
	setExcludeEmptyTenders: (value: boolean) => void;
	excludeFullyInvoiced: boolean;
	setExcludeFullyInvoiced: (value: boolean) => void;
	groupByClient: boolean;
	setGroupByClient: (value: boolean) => void;

	// Pagination
	paginationModel: PaginationModel;
	setPaginationModel: (model: PaginationModel) => void;

	// Actions
	expandRow: (id: string, path: string[], itemType: OverviewItemType) => Promise<void>;
	expandAll: () => Promise<void>;
	refresh: () => void;
};

const mapGqlItemType = (type: string): OverviewItemType => {
	switch (type) {
		case "CLIENT":
			return "CLIENT";
		case "PROJECT":
			return "PROJECT";
		case "EMPLOYEE":
			return "EMPLOYEE";
		case "TIMESHEET":
			return "TIMESHEET";
		default:
			return "PROJECT";
	}
};

const mapToGqlItemType = (type: OverviewItemType): GqlOverviewItemType => {
	switch (type) {
		case "CLIENT":
			return GqlOverviewItemType.Client;
		case "PROJECT":
			return GqlOverviewItemType.Project;
		case "EMPLOYEE":
			return GqlOverviewItemType.Employee;
		case "TIMESHEET":
			return GqlOverviewItemType.Timesheet;
		default:
			return GqlOverviewItemType.Project;
	}
};

const mapNodeToItem = (node: Record<string, unknown>): MonthlyProjectOverviewItem => {
	const clientData = node.client as { id: string; displayName: string } | null;
	const employeeData = node.employee as { id: string; displayName: string } | null;

	return {
		id: node.id as string,
		itemType: mapGqlItemType(node.itemType as string),
		projectId: node.projectId as string | null,
		projectName: node.projectName as string | null,
		projectNumber: node.projectNumber as string | null,
		projectType: node.projectType as MonthlyProjectOverviewItem["projectType"],
		isGroup: (node.isGroup as boolean) ?? false,
		isInternalProjectsGroup: (node.isInternalProjectsGroup as boolean) ?? false,
		budgetStatus: node.budgetStatus as MonthlyProjectOverviewItem["budgetStatus"],
		clientId: node.clientId as string | null,
		clientName: node.clientName as string | null,
		employeeId: node.employeeId as string | null,
		employeeName: node.employeeName as string | null,
		timesheetId: node.timesheetId as string | null,
		timesheetNumber: node.timesheetNumber as string | null,
		timesheetStatus: node.timesheetStatus as string | null,
		timesheetFrom: node.timesheetFrom as string | null,
		timesheetTo: node.timesheetTo as string | null,
		openMinutes: node.openMinutes as number,
		pendingMinutes: node.pendingMinutes as number,
		approvedMinutes: node.approvedMinutes as number,
		totalMinutes: node.totalMinutes as number,
		clientInvoices: (node.clientInvoices as MonthlyProjectOverviewItem["clientInvoices"]) ?? [],
		supplierCreditNotes: (node.supplierCreditNotes as MonthlyProjectOverviewItem["supplierCreditNotes"]) ?? [],
		path: node.path as string[],
		parentId: node.parentId as string | null,
		canCreateInvoice: node.canCreateInvoice as boolean,
		canCreateCreditNote: node.canCreateCreditNote as boolean,
		canCreateMonthlyBilling: node.canCreateMonthlyBilling as boolean | undefined,
		client: clientData ? { id: clientData.id, name: clientData.displayName } : null,
		employee: employeeData ? { id: employeeData.id, name: employeeData.displayName } : null,
		project: node.project as MonthlyProjectOverviewItem["project"],
	};
};

export function useMonthlyOverviewList(): UseMonthlyOverviewListReturn {
	// Data state
	const [rows, setRows] = useState<MonthlyProjectOverviewItem[]>([]);
	const [subRows, setSubRows] = useState<MonthlyProjectOverviewItem[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [matchedProjectIds, setMatchedProjectIds] = useState<string[]>([]);

	// Filter state
	const [month, setMonth] = useState<Dayjs>(dayjs().startOf("month"));
	const [searchValue, setSearchValue] = useState("");
	const debouncedSearchValue = useDebounce(searchValue, 300);
	const [selectedClient, setSelectedClient] = useState<IdWithName | null>(null);
	const [selectedEmployee, setSelectedEmployee] = useState<IdWithName | null>(null);
	const [excludeEmptyTenders, setExcludeEmptyTenders] = useState(true);
	const [excludeFullyInvoiced, setExcludeFullyInvoiced] = useState(false);
	const [groupByClient, setGroupByClient] = useState(false);

	// Pagination state
	const [paginationModel, setPaginationModel] = useState<PaginationModel>({
		page: 0,
		pageSize: 20,
	});

	const { showNotification } = useNotification();

	// Combine rows and subRows for rendering
	const allRows = useMemo(() => [...rows, ...subRows], [rows, subRows]);

	// GraphQL queries
	const [getRoots] = useGetMonthlyOverviewRootsLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	const [getChildren] = useGetMonthlyOverviewChildrenLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
	});

	const [searchOverview] = useSearchMonthlyOverviewLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	// Fetch data based on mode (browse or search)
	const fetchData = useCallback(async () => {
		setIsLoading(true);
		setSubRows([]); // Clear subRows when fetching new data

		const monthStr = month.format("YYYY-MM-DD");

		if (debouncedSearchValue) {
			// Search mode - get full tree with matches + ancestors
			const response = await searchOverview({
				variables: {
					month: monthStr,
					projectName: debouncedSearchValue,
					clientId: selectedClient?.id ?? null,
					employeeId: selectedEmployee?.id ?? null,
					excludeEmptyTenders,
					excludeFullyInvoiced,
					first: paginationModel.pageSize,
					after: paginationModel.page > 0 ? String(paginationModel.pageSize * paginationModel.page) : undefined,
				},
			});

			if (response.data?.searchMonthlyOverview) {
				const items = response.data.searchMonthlyOverview.items.map((node) =>
					mapNodeToItem(node as unknown as Record<string, unknown>),
				);
				setRows(items);
				setRowCount(response.data.searchMonthlyOverview.totalCount);
				setMatchedProjectIds(response.data.searchMonthlyOverview.matchedProjectIds ?? []);
			}
		} else {
			// Browse mode - get only roots
			const response = await getRoots({
				variables: {
					month: monthStr,
					clientId: selectedClient?.id ?? null,
					employeeId: selectedEmployee?.id ?? null,
					excludeEmptyTenders,
					excludeFullyInvoiced,
					groupByClient,
					first: paginationModel.pageSize,
					after: paginationModel.page > 0 ? String(paginationModel.pageSize * paginationModel.page) : undefined,
				},
			});

			if (response.data?.monthlyOverviewRoots) {
				const items = response.data.monthlyOverviewRoots.nodes
					.filter((node): node is NonNullable<typeof node> => node !== null)
					.map((node) => mapNodeToItem(node as unknown as Record<string, unknown>));
				setRows(items);
				setRowCount(response.data.monthlyOverviewRoots.totalCount);
				setMatchedProjectIds([]);
			}
		}

		setIsLoading(false);
	}, [
		month,
		debouncedSearchValue,
		selectedClient,
		selectedEmployee,
		excludeEmptyTenders,
		excludeFullyInvoiced,
		groupByClient,
		paginationModel,
		getRoots,
		searchOverview,
	]);

	// Expand row to load children (lazy loading)
	const expandRow = useCallback(
		async (id: string, path: string[], itemType: OverviewItemType) => {
			console.log("[expandRow] Called with id:", id, "path:", path, "itemType:", itemType);

			// Check if children are already loaded
			const existingChildren = subRows.filter((row) => row.parentId === id);
			if (existingChildren.length > 0) {
				console.log("[expandRow] Children already loaded:", existingChildren.length);
				return; // Already loaded
			}

			const monthStr = month.format("YYYY-MM-DD");
			console.log("[expandRow] Fetching children for month:", monthStr, "parentType:", mapToGqlItemType(itemType));

			try {
				const response = await getChildren({
					variables: {
						parentId: id,
						parentPath: path,
						parentType: mapToGqlItemType(itemType),
						month: monthStr,
						employeeId: selectedEmployee?.id ?? null,
					},
				});

				console.log("[expandRow] Response:", response);

				if (response.data?.monthlyOverviewChildren) {
					const children = response.data.monthlyOverviewChildren.map((node) =>
						mapNodeToItem(node as unknown as Record<string, unknown>),
					);
					console.log("[expandRow] Mapped children:", children.length, children);
					if (children.length > 0) {
						setSubRows((prev) => [...prev, ...children]);
					}
				}

				if (response.errors) {
					console.error("[expandRow] GraphQL errors:", response.errors);
				}
			} catch (error) {
				console.error("[expandRow] Error:", error);
			}
		},
		[month, selectedEmployee, getChildren, subRows],
	);

	// Expand all rows - loads children for all expandable rows
	const expandAll = useCallback(async () => {
		console.log("[expandAll] Starting to expand all rows");

		const monthStr = month.format("YYYY-MM-DD");

		// Collect all expandable rows (rows that can have children)
		const expandableRows = rows.filter(
			(row) => row.isGroup || row.itemType === "CLIENT" || row.itemType === "PROJECT" || row.itemType === "EMPLOYEE",
		);

		console.log("[expandAll] Found expandable rows:", expandableRows.length);

		// Load children for all expandable rows that don't have children yet
		const loadPromises: Promise<void>[] = [];

		for (const row of expandableRows) {
			// Check if children already loaded
			const hasChildren = subRows.some((sub) => sub.parentId === row.id);
			if (!hasChildren) {
				console.log("[expandAll] Loading children for:", row.id, row.itemType, "path:", row.path);
				const promise = (async () => {
					try {
						const response = await getChildren({
							variables: {
								parentId: row.id,
								parentPath: row.path,
								parentType: mapToGqlItemType(row.itemType),
								month: monthStr,
								employeeId: selectedEmployee?.id ?? null,
							},
						});

						if (response.data?.monthlyOverviewChildren) {
							const children = response.data.monthlyOverviewChildren.map((node) =>
								mapNodeToItem(node as unknown as Record<string, unknown>),
							);
							console.log("[expandAll] Loaded", children.length, "children for", row.id);
							if (children.length > 0) {
								setSubRows((prev) => [...prev, ...children]);
							}
						}
					} catch (error) {
						console.error("[expandAll] Error loading children for", row.id, error);
					}
				})();
				loadPromises.push(promise);
			}
		}

		// Wait for all children to load
		await Promise.all(loadPromises);
		console.log("[expandAll] All children loaded");
	}, [rows, subRows, month, selectedEmployee, getChildren]);

	// Refresh data
	const refresh = useCallback(() => {
		fetchData();
	}, [fetchData]);

	// Initial fetch and refetch when filters change
	useEffect(() => {
		fetchData();
	}, [
		month,
		debouncedSearchValue,
		selectedClient,
		selectedEmployee,
		excludeEmptyTenders,
		excludeFullyInvoiced,
		groupByClient,
		paginationModel,
	]);

	return {
		// Data
		rows,
		subRows,
		allRows,
		rowCount,
		isLoading,
		matchedProjectIds,

		// Filters
		month,
		setMonth,
		searchValue,
		setSearchValue,
		debouncedSearchValue,
		selectedClient,
		setSelectedClient,
		selectedEmployee,
		setSelectedEmployee,
		excludeEmptyTenders,
		setExcludeEmptyTenders,
		excludeFullyInvoiced,
		setExcludeFullyInvoiced,
		groupByClient,
		setGroupByClient,

		// Pagination
		paginationModel,
		setPaginationModel,

		// Actions
		expandRow,
		expandAll,
		refresh,
	};
}
